/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.sessions

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import studio.oddpods.cauce.core.base.ui.BaseViewModel
import studio.oddpods.cauce.core.data.repository.cauce.RecurringScheduleRepository
import studio.oddpods.cauce.core.formatter.CurrencyFormatter
import studio.oddpods.cauce.core.formatter.DateFormatter
import studio.oddpods.cauce.core.model.session.RecurrenceFrequency
import studio.oddpods.cauce.core.model.session.UpdateRecurringScheduleRequest
import studio.oddpods.cauce.core.ui.UiText
import studio.oddpods.cauce.core.ui.toUiText
import studio.oddpods.cauce.feature.sessions.generated.resources.Res
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_edit_error_load
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_error_fee_invalid
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_error_interval_invalid
import studio.oddpods.cauce.feature.sessions.generated.resources.recurring_schedule_error_save_failed

class EditRecurringScheduleViewModel(
    private val scheduleId: String,
    val patientName: String,
    private val recurringScheduleRepository: RecurringScheduleRepository,
    private val currencyFormatter: CurrencyFormatter,
    val dateFormatter: DateFormatter,
) : BaseViewModel<CreateRecurringScheduleState, CreateRecurringScheduleEvent, CreateRecurringScheduleAction>(
    CreateRecurringScheduleState(),
) {

    init {
        loadSchedule()
    }

    override fun handleAction(action: CreateRecurringScheduleAction) {
        when (action) {
            is CreateRecurringScheduleAction.PatientSelected -> Unit
            is CreateRecurringScheduleAction.FrequencyChanged ->
                mutableStateFlow.value = state.copy(frequency = action.frequency, error = null)
            is CreateRecurringScheduleAction.AnchorDateChanged ->
                mutableStateFlow.value = state.copy(anchorDate = action.date, error = null)
            is CreateRecurringScheduleAction.FeeChanged ->
                mutableStateFlow.value = state.copy(feeInput = action.fee, error = null)
            is CreateRecurringScheduleAction.IntervalDaysChanged ->
                mutableStateFlow.value = state.copy(intervalDaysInput = action.days, error = null)
            CreateRecurringScheduleAction.SaveClicked -> handleSave()
        }
    }

    private fun loadSchedule() {
        viewModelScope.launch {
            recurringScheduleRepository.getSchedule(scheduleId)
                .onSuccess { schedule ->
                    mutableStateFlow.value = state.copy(
                        frequency = schedule.frequency,
                        anchorDate = LocalDate.parse(schedule.anchorDate),
                        feeInput = (schedule.fee / 100).toString(),
                        intervalDaysInput = schedule.intervalDays?.toString() ?: "",
                        isLoadingPatients = false,
                    )
                }
                .onFailure { e ->
                    mutableStateFlow.value = state.copy(
                        isLoadingPatients = false,
                        error = e.toUiText(Res.string.recurring_schedule_edit_error_load),
                    )
                }
        }
    }

    private fun handleSave() {
        val feeCentavos = currencyFormatter.parse(state.feeInput)
        val customIntervalDays = state.intervalDaysInput.toIntOrNull()?.takeIf { it > 0 }
        val validationError = when {
            feeCentavos == null || feeCentavos <= 0 ->
                UiText.Resource(Res.string.recurring_schedule_error_fee_invalid)
            state.frequency == RecurrenceFrequency.CUSTOM && customIntervalDays == null ->
                UiText.Resource(Res.string.recurring_schedule_error_interval_invalid)
            else -> null
        }
        if (validationError != null) {
            mutableStateFlow.value = state.copy(error = validationError)
            return
        }
        val intervalDays = if (state.frequency == RecurrenceFrequency.CUSTOM) customIntervalDays else null

        mutableStateFlow.value = state.copy(isSaving = true, error = null)
        viewModelScope.launch {
            recurringScheduleRepository.updateSchedule(
                scheduleId = scheduleId,
                request = UpdateRecurringScheduleRequest(
                    frequency = state.frequency,
                    intervalDays = intervalDays,
                    dayOfWeek = state.anchorDate.dayOfWeek.ordinal + 1,
                    fee = feeCentavos!!,
                    anchorDate = state.anchorDate.toString(),
                ),
            )
                .onSuccess {
                    mutableStateFlow.value = state.copy(isSaving = false)
                    sendEvent(CreateRecurringScheduleEvent.SaveSuccess)
                }
                .onFailure { e ->
                    mutableStateFlow.value = state.copy(
                        isSaving = false,
                        error = e.toUiText(Res.string.recurring_schedule_error_save_failed),
                    )
                }
        }
    }
}
